// ============================================================================
// Constants
// ============================================================================

const ID_TO_NAME_MAP = {
  firmId: 'firm_name',
  firm_id: 'firm_name',
  contract_subject_id: 'contract_subject_name',
  scope_of_work_id: 'scope_of_work_name',
  pricing_method_id: 'pricing_method_name',
  work_status_id: 'work_status_name',
  companyId: 'company_name',
  company_id: 'company_name',
  groupId: 'group_name',
  group_id: 'group_name',
  teamId: 'team_name',
  team_id: 'team_name',
  titleId: 'title_name',
  title_id: 'title_name',
  partner_ids: 'partnerNames',
  sector_ids: 'sectorNames',
  partner_id: 'partner_name',
  sector_id: 'sector_name',
  leaveType: 'leaveTypeName',
  leave_type: 'leaveTypeName',
  expenseType: 'expenseTypeName',
  expense_type: 'expenseTypeName',
  invoice_code_id: 'invoice_code_name',
  user_id: 'user_full_name',
  approving_manager_id: 'approving_manager_full_name',
};

const PLACEHOLDER_PREFIXES = ['Subject ', 'Scope ', 'Pricing '];

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format Turkish Lira amount
 */
function formatTRY(n) {
  if (n === 0) return '0,00';
  if (n === null || n === undefined) return '';
  const v = Number(n);
  if (Number.isNaN(v)) return '';
  return v.toLocaleString('tr-TR', { minimumFractionDigits: 2 });
}

/**
 * Get YYYY-MM key from date
 */
function yyyymmKey(dateLike) {
  const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Get Turkish month label from date
 */
function monthLabelTR(dateLike) {
  const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
  return d.toLocaleDateString('tr-TR', { year: 'numeric', month: 'long' });
}

/**
 * Add months to a date
 */
function addMonths(dateLike, months) {
  const d = dateLike instanceof Date ? new Date(dateLike) : new Date(dateLike);
  if (Number.isNaN(d.getTime())) return null;
  const result = new Date(d);
  result.setMonth(result.getMonth() + Number(months || 0));
  return result;
}

/**
 * Get project number from job details
 */
function getProjectNumberFromJobDetails(job) {
  if (!job) return '';
  // 1) jobs tablosunun direkt kolonu — Robosan TÜBİTAK, TEKNOPARK gibi
  //    islerde burada "7260428" gibi bir deger var.
  if (job.project_number) return String(job.project_number);
  // 2) Fallback: job_details.detail_data.projectNumber (JSONB icinde saklanan
  //    daha eski/manuel girisler icin).
  if (Array.isArray(job.job_details)) {
    for (let i = job.job_details.length - 1; i >= 0; i -= 1) {
      const pn = job.job_details[i]?.detail_data?.projectNumber;
      if (pn) return String(pn);
    }
  }
  return '';
}

/**
 * Extract text from React element recursively
 */
function extractTextFromReactElement(element) {
  if (element === null || element === undefined) return '';
  if (typeof element === 'string' || typeof element === 'number') {
    return String(element);
  }
  if (Array.isArray(element)) {
    return element.map(extractTextFromReactElement).filter(Boolean).join(' ');
  }
  if (typeof element === 'object' && element.props) {
    if (element.props.label) return extractTextFromReactElement(element.props.label);
    if (element.props.children) return extractTextFromReactElement(element.props.children);
    if (element.props.text) return String(element.props.text);
    if (element.props.value) return String(element.props.value);
  }
  if (typeof element === 'object') {
    if (element.label) return String(element.label);
    if (element.text) return String(element.text);
    if (element.value) return String(element.value);
    if (element.name) return String(element.name);
    return '';
  }
  return String(element);
}

/**
 * Get value from object by path (supports nested paths like "user.first_name")
 */
function getValueByPath(obj, path) {
  if (!path) return null;
  const parts = path.split('.');
  let value = obj;
  for (const part of parts) {
    if (value === null || value === undefined) return null;
    value = value[part];
  }
  return value;
}

/**
 * Check if a string starts with any of the placeholder prefixes
 */
function isPlaceholderValue(value) {
  if (typeof value !== 'string') return false;
  return PLACEHOLDER_PREFIXES.some(prefix => value.startsWith(prefix));
}

// ============================================================================
// Field Resolution Functions
// ============================================================================

/**
 * Resolve label from option by ID
 */
function resolveLabelFromOption(idValue, options, t) {
  if (!options || !Array.isArray(options)) return null;
  const option = options.find(opt => String(opt.value) === String(idValue));
  if (!option) return null;
  return t && option.labelKey ? t(option.labelKey) : (option.label || idValue);
}

/**
 * Resolve array field values to labels
 */
function resolveArrayFieldValue(idArray, options, t) {
  if (!Array.isArray(idArray) || idArray.length === 0) return '';
  if (!options || !Array.isArray(options)) {
    return idArray.join(', ');
  }
  const labels = idArray.map(id => {
    const label = resolveLabelFromOption(id, options, t);
    return label || id;
  });
  return labels.join(', ');
}

/**
 * Resolve ID field to name/label
 */
function resolveIdFieldValue(field, row, t) {
  const { nameField, field: fieldName, options } = field;
  
  // First, try to get name from corresponding name field in data
  const nameValue = getValueByPath(row, nameField);
  if (nameValue && nameValue !== '' && !isPlaceholderValue(nameValue)) {
    return nameValue;
  }
  
  // If name field doesn't exist, try to resolve from options
  const idValue = getValueByPath(row, fieldName);
  
  // Handle array fields (like partner_ids, sector_ids)
  if (Array.isArray(idValue) && idValue.length > 0) {
    return resolveArrayFieldValue(idValue, options, t);
  }
  
  // Handle single ID value
  if (typeof idValue === 'number' || typeof idValue === 'string') {
    const label = resolveLabelFromOption(idValue, options, t);
    return label || idValue || '';
  }
  
  return idValue || '';
}

/**
 * Resolve select field value to label
 */
function resolveSelectFieldValue(value, options, t) {
  if (value === null || value === undefined) return value;
  if (typeof value !== 'number' && typeof value !== 'string') return value;
  
  const label = resolveLabelFromOption(value, options, t);
  return label || value;
}

/**
 * Format cell value for export
 */
function formatCellValue(value, field, row, t) {
  // Handle null/undefined
  if (value === null || value === undefined) return '';
  
  // Handle dates
  if (value instanceof Date) {
    return value.toLocaleDateString('tr-TR');
  }
  
  // Handle booleans
  if (typeof value === 'boolean') {
    return value ? 'Evet' : 'Hayır';
  }
  
  // Handle objects
  if (typeof value === 'object') {
    // Try to extract meaningful string representation
    if (value.label) return String(value.label);
    if (value.name) return String(value.name);
    if (value.text) return String(value.text);
    if (value.value) return String(value.value);
    
    // Handle user objects
    if (value.first_name || value.last_name) {
      return [value.first_name, value.last_name].filter(Boolean).join(' ');
    }
    
    // Handle arrays
    if (Array.isArray(value)) {
      return value.map(v => {
        if (typeof v === 'object' && v.label) return v.label;
        if (typeof v === 'object' && v.name) return v.name;
        return String(v);
      }).join(', ');
    }
    
    // Return empty to avoid [object Object]
    return '';
  }
  
  return String(value);
}

/**
 * Get cell value for a field
 */
function getCellValue(field, row, t) {
  let value = null;
  
  // Handle ID fields
  if (field.isIdField && field.nameField) {
    value = resolveIdFieldValue(field, row, t);
  } else {
    // Get value using field path
    value = getValueByPath(row, field.field);
    
    // Resolve select field labels
    if (field.type === 'select' && field.options) {
      value = resolveSelectFieldValue(value, field.options, t);
    }
  }
  
  // Ham CSV degeri icin field-level override: kolon `exportValue(row, value)`
  // tanimlamissa render'i tamamen atlar. Excel'de sayi olarak islemek istenen
  // hucreler (tutar, hedef ciro) icin kullanilir — aksi halde `formatTL`
  // gibi render'lar "1.234,50 ₺" gonderiyor ve Excel bunu string olarak
  // tutuyor.
  if (typeof field.exportValue === 'function') {
    try {
      const raw = field.exportValue(row, value, t);
      return raw == null ? '' : String(raw);
    } catch (error) {
      console.warn(`Error in exportValue for column ${field.field}:`, error);
    }
  }

  // Use custom render function if available
  if (field.render && typeof field.render === 'function') {
    try {
      const rendered = field.render(value, row, null, t);
      return extractTextFromReactElement(rendered);
    } catch (error) {
      console.warn(`Error rendering column ${field.field}:`, error);
      return extractTextFromReactElement(value);
    }
  }
  
  // Format and return value
  return formatCellValue(value, field, row, t);
}

// ============================================================================
// Field Definition Builders
// ============================================================================

/**
 * Check if field should be included in export
 */
function shouldIncludeField(field, columnFields, data, idToNameMap) {
  // Skip if already in columns
  if (columnFields.includes(field.name)) return false;
  
  // For ID fields, check if corresponding name field exists
  const nameField = idToNameMap[field.name];
  if (nameField) {
    // If name field is in columns, skip the ID field
    if (columnFields.includes(nameField)) return false;
    // If name field exists in data, skip ID field
    if (data.length > 0 && data[0][nameField]) return false;
  }
  
  return true;
}

/**
 * Build additional fields from formFields
 */
function buildAdditionalFields(formFields, columnFields, data, idToNameMap) {
  if (!Array.isArray(formFields)) return [];
  
  return formFields
    .filter(field => shouldIncludeField(field, columnFields, data, idToNameMap))
    .map(field => ({
      field: field.name,
      headerName: field.label || field.name,
      type: field.type,
      options: field.options,
      isIdField: idToNameMap[field.name] !== undefined,
      nameField: idToNameMap[field.name],
    }));
}

/**
 * Build all field definitions (columns + additional fields)
 */
function buildFieldDefinitions(columns, formFields, data, idToNameMap) {
  const columnFields = columns
    .filter(col => col.field && !col.hideInExport)
    .map(col => col.field);
  
  const additionalFields = buildAdditionalFields(formFields, columnFields, data, idToNameMap);
  
  // Combine columns and additional fields, remove duplicates
  const allFieldsMap = new Map();
  
  columns
    .filter(col => col.field && !col.hideInExport)
    .forEach(col => {
      allFieldsMap.set(col.field, col);
    });
  
  additionalFields.forEach(field => {
    if (!allFieldsMap.has(field.field)) {
      allFieldsMap.set(field.field, field);
    }
  });
  
  return Array.from(allFieldsMap.values());
}

/**
 * Build headers from field definitions
 */
function buildHeaders(allFields, t) {
  return allFields.map(field => {
    if (t && field.headerName) {
      const translated = t(field.headerName);
      return translated !== field.headerName ? translated : (field.headerName || field.field);
    }
    return field.headerName || field.field;
  });
}

// ============================================================================
// CSV Generation Functions
// ============================================================================

/**
 * Escape CSV cell value
 */
function escapeCSVCell(value) {
  const s = String(value ?? '').replace(/"/g, '""');
  return /[";\n,]/.test(s) ? `"${s}"` : s;
}

/**
 * Generate CSV content from headers and rows
 */
function generateCSVContent(headers, rows) {
  const csvRows = [headers, ...rows].map(row =>
    row.map(escapeCSVCell).join(';')
  );
  return csvRows.join('\n');
}

/**
 * Download CSV file
 */
function downloadCSV(csvContent, filename) {
  const bom = new Uint8Array([0xEF, 0xBB, 0xBF]); // UTF-8 BOM for Excel
  const blob = new Blob([bom, csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || `export_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ============================================================================
// Main Export Functions
// ============================================================================

/**
 * General CSV/Excel export function for data tables
 * @param {Array} data - Array of data objects to export
 * @param {Array} columns - Array of column definitions with field, headerName, and optional render function
 * @param {string} filename - Optional filename (default: 'export_YYYY-MM-DD.csv')
 * @param {Function} t - Translation function (optional)
 * @param {Array} formFields - Optional form fields to include additional data not in columns
 */
export function exportTableToCSV(data = [], columns = [], filename = null, t = null, formFields = []) {
  // Validation
  if (!Array.isArray(data) || data.length === 0) {
    console.warn('No data to export');
    return;
  }
  
  if (!Array.isArray(columns) || columns.length === 0) {
    console.warn('No columns defined for export');
    return;
  }
  
  // Build field definitions
  const allFields = buildFieldDefinitions(columns, formFields, data, ID_TO_NAME_MAP);
  
  // Build headers
  const headers = buildHeaders(allFields, t);
  
  // Build rows
  const rows = data.map(row =>
    allFields.map(field => getCellValue(field, row, t))
  );
  
  // Generate and download CSV
  const csvContent = generateCSVContent(headers, rows);
  downloadCSV(csvContent, filename);
}

// ============================================================================
// Invoice Report Export
// ============================================================================

/**
 * Collect all unique month keys from invoices
 */
function collectMonthKeys(invoices) {
  const monthKeySet = new Set();
  
  invoices.forEach(inv => {
    // V1 format: flat payment_schedules
    const schedules = Array.isArray(inv.payment_schedules) ? inv.payment_schedules : [];
    schedules.forEach(s => {
      if (s && s.is_active !== false && !s.is_transfer) {
        const year = Number(s.year);
        const month = Number(s.month);
        if (!Number.isNaN(year) && !Number.isNaN(month) && month >= 1 && month <= 12) {
          monthKeySet.add(yyyymmKey(new Date(year, month - 1, 1)));
        }
      }
    });
    
    // V2 format: split invoice codes each with its own schedules
    const splitCodes = Array.isArray(inv.invoice_code_ids) ? inv.invoice_code_ids : [];
    splitCodes.forEach(code => {
      const codeSchedules = Array.isArray(code?.schedules) ? code.schedules : [];
      codeSchedules.forEach(s => {
        if (s && s.is_active !== false && !s.is_transfer) {
          const year = Number(s.year);
          const month = Number(s.month);
          if (!Number.isNaN(year) && !Number.isNaN(month) && month >= 1 && month <= 12) {
            monthKeySet.add(yyyymmKey(new Date(year, month - 1, 1)));
          }
        }
      });
    });
  });
  
  return Array.from(monthKeySet).sort();
}

/**
 * Build month key to label mapping
 */
function buildMonthLabelMap(monthKeys) {
  return Object.fromEntries(
    monthKeys.map(k => {
      const [y, m] = k.split('-');
      return [k, monthLabelTR(new Date(Number(y), Number(m) - 1, 1)).toUpperCase()];
    })
  );
}

/**
 * Calculate month totals from schedules
 */
function calculateMonthTotals(schedules, sortedMonthKeys) {
  const monthTotals = {};
  
  schedules.forEach(s => {
    if (s && s.is_active !== false && !s.is_transfer) {
      const year = Number(s.year);
      const month = Number(s.month);
      const amount = Number(s.amount) || 0;
      if (!Number.isNaN(year) && !Number.isNaN(month)) {
        const key = yyyymmKey(new Date(year, month - 1, 1));
        monthTotals[key] = (monthTotals[key] || 0) + amount;
      }
    }
  });
  
  return sortedMonthKeys.map(k => formatTRY(monthTotals[k] || 0));
}

/**
 * Get invoice code label
 */
function getInvoiceCodeLabel(codeId, invoiceCodes) {
  const matched = Array.isArray(invoiceCodes)
    ? invoiceCodes.find(c => String(c.id) === String(codeId))
    : null;
  if (!matched) return '';
  
  const codeName = matched?.code_name || '';
  const productName = matched?.product_name || '';
  return [codeName, productName].filter(Boolean).join(' - ');
}

/**
 * Bir sozlesmenin BEKLENEN otomatik yenileme tarihini dondurur.
 *
 * Kural (useContracts.js:computeRenewalStatus ile ayni):
 *  1) auto_renewal = false ise → yenileme yok, bos string.
 *  2) contract_renewals tablosunda kayit varsa → EN SON renewal_end_date
 *     (adam her yıl yeniden yenilediyse, next renewal en son yenilemenin
 *     bitis tarihidir; ondan sonra yeni bir renewal ihtiyaci olur).
 *  3) Hic renewal kaydi yoksa (ilk kez yenileme yaklaşıyor) →
 *     contract_signing_date + renewal_period ay.
 *
 * Onceki kod sadece adim (3)'ü uyguluyordu, dolayısıyla 2019 imzali sozlesme
 * icin 2020 tarihi donuyordu, ama gercek renewals kayitlarina bakip 2027'ye
 * kadar yenilenmis oldugunu goremiyordu.
 */
function getAutoRenewalDate(contract, invoice) {
  if (!contract?.auto_renewal) return '';

  const renewals = Array.isArray(contract.renewals) ? contract.renewals : [];

  // Fatura icin referans tarih: kendi donem sonu > donem baslangici > olusma tarihi
  const invoiceRefDateStr =
    invoice?.date_range_end ||
    invoice?.date_range_start ||
    invoice?.created_at ||
    null;
  const invoiceRefMs = invoiceRefDateStr
    ? new Date(invoiceRefDateStr).getTime()
    : NaN;

  // Debug: hangi fallback devrede oldugunu ve verinin ne oldugunu Console'da goster.
  // eslint-disable-next-line no-console
  console.log('[Export-AutoRenewal]', {
    invoiceId: invoice?.id,
    invoice_date_range_start: invoice?.date_range_start,
    invoice_date_range_end: invoice?.date_range_end,
    invoice_created_at: invoice?.created_at,
    custom_date_range: invoice?.custom_date_range,
    invoiceRefDateStr,
    renewalsCount: renewals.length,
    renewals: renewals.map((r) => ({
      start: r?.renewal_start_date,
      end: r?.renewal_end_date,
    })),
    contract_signing_date: contract?.contract_signing_date,
    contract_renewal_period: contract?.renewal_period,
  });

  // Fatura icin BEKLENEN yenileme tarihi = fatura hangi donemdeyse, o donemin
  // BITISTEN SONRAKI ilk gunu (= bir sonraki yenilemenin baslangici).
  // Kullanici mantigi: "yenileme tarihi = yenilemenin YAPILDIGI gun."
  //
  // EDS ornegi:
  //   - Fatura 25 (2026-03-13, renewal 103 aralığı olan 2026-06-01 → 2027-05-31'in
  //     ONCESINDE): sonraki renewal_start = 2026-06-01 → "1 Haziran 2026".
  //   - Fatura 102 (2026-06-30, renewal 103 icinde): renewal_end (2027-05-31) + 1 gun
  //     = 2027-06-01 → "1 Haziran 2027".
  if (Number.isFinite(invoiceRefMs) && renewals.length > 0) {
    // 1) Fatura renewal araliginda mi? → renewal_end_date + 1 gun.
    const matching = renewals.find((r) => {
      const s = r?.renewal_start_date ? new Date(r.renewal_start_date).getTime() : NaN;
      const e = r?.renewal_end_date ? new Date(r.renewal_end_date).getTime() : NaN;
      return (
        Number.isFinite(s) && Number.isFinite(e)
        && invoiceRefMs >= s && invoiceRefMs <= e
      );
    });
    if (matching?.renewal_end_date) {
      const nextRenewal = new Date(matching.renewal_end_date);
      nextRenewal.setDate(nextRenewal.getDate() + 1);
      if (!Number.isNaN(nextRenewal.getTime())) {
        return nextRenewal.toLocaleDateString('tr-TR');
      }
    }

    // 2) Fatura hicbir renewal araliginda degil ama gelecek renewal_start_date
    // varsa: o dogrudan yenileme tarihidir.
    let earliestFutureStartMs = null;
    for (const r of renewals) {
      const s = r?.renewal_start_date ? new Date(r.renewal_start_date).getTime() : NaN;
      if (!Number.isFinite(s)) continue;
      if (s > invoiceRefMs && (earliestFutureStartMs == null || s < earliestFutureStartMs)) {
        earliestFutureStartMs = s;
      }
    }
    if (earliestFutureStartMs != null) {
      const d = new Date(earliestFutureStartMs);
      return d.toLocaleDateString('tr-TR');
    }
  }

  // 2) Fatura kendi donem_bitisini biliyorsa (renewal ile eslesmedi ama fatura
  //    kendini "2025-06 -> 2026-05" seklinde tanimlamis).
  if (invoice?.date_range_end) {
    const d = new Date(invoice.date_range_end);
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString('tr-TR');
  }

  // 3) En son renewal (fallback — global "son yenileme").
  if (renewals.length > 0) {
    const sorted = [...renewals].sort((a, b) => {
      const ta = a?.renewal_end_date ? new Date(a.renewal_end_date).getTime() : 0;
      const tb = b?.renewal_end_date ? new Date(b.renewal_end_date).getTime() : 0;
      return tb - ta;
    });
    const lastEnd = sorted[0]?.renewal_end_date;
    if (lastEnd) {
      const d = new Date(lastEnd);
      if (!Number.isNaN(d.getTime())) return d.toLocaleDateString('tr-TR');
    }
  }

  // 4) Hic renewal yok → signing + period (ilk kez yenileme yaklasiyor).
  const signingDate = contract.contract_signing_date;
  const renewalPeriod = contract.renewal_period;
  if (!signingDate || !renewalPeriod) return '';
  const renewed = addMonths(signingDate, parseInt(renewalPeriod, 10));
  return renewed ? renewed.toLocaleDateString('tr-TR') : '';
}

/**
 * Build invoice report rows
 */
function buildInvoiceReportRows(invoices, invoiceCodes, sortedMonthKeys) {
  const rows = [];
  
  invoices.forEach(inv => {
    const firmName = inv.contract?.firm?.firm_name || '';
    const faturaAdi = inv.invoice_name || '';
    const isTuru = inv.job?.job_type || '';
    const projeNo = getProjectNumberFromJobDetails(inv.job);
    const otoYenileme = getAutoRenewalDate(inv.contract, inv);
    
    const splitCodes = Array.isArray(inv.invoice_code_ids) ? inv.invoice_code_ids : [];
    
    if (splitCodes.length > 0) {
      // Her fatura kodu için ayrı satır
      splitCodes.forEach(codeItem => {
        const codeId = codeItem?.invoice_code_id || codeItem?.id || codeItem;
        const codeLabel = getInvoiceCodeLabel(codeId, invoiceCodes);
        const codeSchedules = Array.isArray(codeItem?.schedules) ? codeItem.schedules : [];
        const monthCells = calculateMonthTotals(codeSchedules, sortedMonthKeys);
        rows.push([firmName, faturaAdi, isTuru, projeNo, codeLabel, otoYenileme, ...monthCells]);
      });
    } else {
      // Tek satır: flat schedule veya tek kod
      const flatCodeId = inv.invoice_code_id;
      const faturaKodu = flatCodeId ? getInvoiceCodeLabel(flatCodeId, invoiceCodes) : '';
      const schedulesFlat = Array.isArray(inv.payment_schedules) ? inv.payment_schedules : [];
      const monthCells = calculateMonthTotals(schedulesFlat, sortedMonthKeys);
      rows.push([firmName, faturaAdi, isTuru, projeNo, faturaKodu, otoYenileme, ...monthCells]);
    }
  });
  
  return rows;
}

/**
 * Export invoices report to CSV
 */
export function exportInvoicesReportCSV(invoices = [], invoiceCodes = []) {
  // Collect month keys
  const sortedMonthKeys = collectMonthKeys(invoices);
  const monthKeyToLabel = buildMonthLabelMap(sortedMonthKeys);
  
  // Build headers
  const headers = [
    'Firma Adı',
    'Fatura Adı',
    'İşin Türü',
    'Proje Numarası',
    'Fatura Kodu',
    'Otomatik Yenileme Tarihi',
    ...sortedMonthKeys.map(k => monthKeyToLabel[k])
  ];
  
  // Build rows
  const rows = buildInvoiceReportRows(invoices, invoiceCodes, sortedMonthKeys);
  
  // Generate and download CSV
  const csvContent = generateCSVContent(headers, rows);
  downloadCSV(csvContent, `Faturalar_Raporu_${new Date().toISOString().slice(0, 10)}.csv`);
}
